import {
  MatrixSettingsContextProvider,
  MatrixVariantsContextProvider,
  useMatrixSettingsContext,
} from '@app/components/products/ProductMatrix/context';
import { InvalidMatrixInfo } from '@app/components/products/ProductMatrix/InvalidMatrixInfo';
import { UnitSwitcher } from '@app/components/products/UnitSwitcher';
import { getIsTwoDimensional, hasOneDimensionOverlap } from '@app/logic/sizeCharts';
import { isSwitchAvailable, switchProductUnit } from '@app/logic/unitsSwitch';
import { useUnitSwitcher } from '@app/utils/hooks/unitSwitcher/useUnitSwitcher';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getHasValidConfigurator } from '@app/utils/matrix';
import { Product } from '@typings';
import { groupWith } from 'ramda';
import React from 'react';

import { LookbookMatrix } from '../LookbookMatrix';

interface Props {
  defaultLocalizedChart?: string;
  tableMappings?: Product.UnitMappings;
  variants: Product.Standard[];
}

const LookbookProductDetailsMatricesComponent = ({ variants }: Pick<Props, 'variants'>) => {
  const { isDesktop, productConfiguration } = useMatrixSettingsContext();

  const hasInvalidConfigurator = isDefined(productConfiguration) && !getHasValidConfigurator(productConfiguration);

  const variantGroups = groupWith(
    (a, b) =>
      !isDesktop ?
        a.variant === b.variant
      : !getIsTwoDimensional(a.itemTable) && !getIsTwoDimensional(b.itemTable) && hasOneDimensionOverlap(a.itemTable, b.itemTable),
    variants,
  );

  if (hasInvalidConfigurator) {
    return <InvalidMatrixInfo />;
  }

  if (isEmpty(variants)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {variantGroups.map((groupVariants, index) => (
        <MatrixVariantsContextProvider key={index} variants={groupVariants}>
          <LookbookMatrix />
        </MatrixVariantsContextProvider>
      ))}
    </div>
  );
};

export const LookbookProductDetailsMatrices = ({ variants, tableMappings, defaultLocalizedChart }: Props) => {
  const [firstVariant] = variants;

  const { selectedUnits } = useUnitSwitcher();

  const variantsWithMappedUnits = React.useMemo(
    () => variants.map(variant => switchProductUnit(variant, selectedUnits)),
    [variants, selectedUnits],
  );

  const sizeSwitcher = isSwitchAvailable(tableMappings) && (
    <div className="flex justify-end">
      <UnitSwitcher tableMappings={tableMappings} defaultUnit={defaultLocalizedChart} />
    </div>
  );

  return (
    <div className="flex max-w-full flex-col gap-4">
      {sizeSwitcher}
      <MatrixSettingsContextProvider productConfiguration={firstVariant?.configuration}>
        <LookbookProductDetailsMatricesComponent variants={variantsWithMappedUnits} />
      </MatrixSettingsContextProvider>
    </div>
  );
};
